import React, { useState } from 'react';
import { getMediaUrl } from '../utils/mediaUrl';
import { openExternalUrl } from '../utils/nativeBridge';

/**
 * Decode HTML numeric entities so Arabic and other Unicode display correctly
 * (handles previews that were stored with raw entities, e.g. &#x643; &#x200e;).
 */
function decodeHtmlEntities(str) {
  if (!str || typeof str !== 'string') return str;
  return str
    .replace(/&#(\d+);?/g, (_, code) => {
      const n = parseInt(code, 10);
      return n <= 0x10ffff ? String.fromCodePoint(n) : '';
    })
    .replace(/&#x([0-9a-fA-F]+);?/g, (_, code) => {
      const n = parseInt(code, 16);
      return n <= 0x10ffff ? String.fromCodePoint(n) : '';
    })
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

// Instagram / Facebook / X posts (server/utils/fetchLinkPreview.js adds `platform`)
const PLATFORMS = {
  instagram: { name: 'Instagram', icon: 'fa-instagram', color: 'text-[#E1306C]' },
  facebook: { name: 'Facebook', icon: 'fa-facebook', color: 'text-[#1877F2]' },
  x: { name: 'X', icon: 'fa-x-twitter', color: 'text-zinc-900 dark:text-white' },
};

const openLink = (url, onClick) => (e) => {
  e.preventDefault();
  e.stopPropagation();
  if (onClick) onClick(e);
  if (url) openExternalUrl(url);
};

/** A post from another network, shown the way it looks there: author, text, photo. */
const SocialPostPreview = ({ preview, onClick }) => {
  const platform = PLATFORMS[preview.platform];
  const [imageFailed, setImageFailed] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const author = decodeHtmlEntities(preview.author) || platform.name;
  const text = decodeHtmlEntities(preview.text);
  const image = preview.image && !imageFailed ? getMediaUrl(preview.image) : null;
  const avatar = preview.avatar && !avatarFailed ? getMediaUrl(preview.avatar) : null;

  return (
    <a
      href={preview.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={openLink(preview.url, onClick)}
      className="block mt-2 border border-zinc-200 dark:border-zinc-800 rounded-2xl overflow-hidden hover:bg-zinc-50/60 dark:hover:bg-zinc-900/60 transition-colors"
    >
      <div className="flex items-center gap-2.5 px-3.5 pt-3">
        {avatar ? (
          <img src={avatar} alt="" onError={() => setAvatarFailed(true)} className="w-9 h-9 rounded-full object-cover shrink-0 bg-zinc-100 dark:bg-zinc-800" loading="lazy" />
        ) : (
          <span className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 font-semibold text-sm">
            {author.charAt(0).toUpperCase()}
          </span>
        )}
        <span className="flex-1 min-w-0 leading-tight" dir="auto">
          <span className="block text-[15px] font-semibold text-zinc-900 dark:text-zinc-100 truncate">{author}</span>
          {preview.handle && (
            <span className="block text-[13px] text-zinc-500 dark:text-zinc-400 truncate" dir="ltr">@{preview.handle}</span>
          )}
        </span>
        <i className={`fab ${platform.icon} ${platform.color} text-xl shrink-0`} aria-label={platform.name} />
      </div>

      {text && (
        <p className="px-3.5 pt-2 text-[15px] leading-relaxed text-zinc-800 dark:text-zinc-200 whitespace-pre-line break-words line-clamp-6" dir="auto">
          {text}
        </p>
      )}

      {image && (
        <div className="mt-2.5 mx-3.5 rounded-xl overflow-hidden bg-zinc-100 dark:bg-zinc-900">
          <img src={image} alt="" onError={() => setImageFailed(true)} className="w-full max-h-[480px] object-cover" loading="lazy" />
        </div>
      )}

      <p className="flex items-center gap-1.5 px-3.5 py-3 text-[13px] font-medium text-zinc-500 dark:text-zinc-400">
        <i className={`fab ${platform.icon} text-xs`} />
        View on {platform.name}
        <i className="fas fa-arrow-up-right-from-square text-[10px] rtl:-scale-x-100" />
      </p>
    </a>
  );
};

/**
 * Open Graph link preview card: image, title, description, and clickable link.
 * Shown below post content when post has linkPreview (and link remains in text).
 * Posts from Instagram, Facebook and X get an embedded-post card instead.
 */
const LinkPreview = ({ linkPreview, preview: previewProp, onClick }) => {
  const preview = linkPreview || previewProp;
  if (!preview || !preview.url) return null;
  if (PLATFORMS[preview.platform]) return <SocialPostPreview preview={preview} onClick={onClick} />;
  return <WebLinkPreview preview={preview} onClick={onClick} />;
};

const WebLinkPreview = ({ preview, onClick }) => {
  // Older previews can point at images that have since expired
  const [imageFailed, setImageFailed] = useState(false);
  const { url, title, description, siteName } = preview;
  const image = imageFailed ? null : preview.image;
  const decodedTitle = decodeHtmlEntities(title);
  const decodedDescription = description ? decodeHtmlEntities(description) : '';
  const decodedSiteName = siteName ? decodeHtmlEntities(siteName) : '';

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={openLink(url, onClick)}
      className="block mt-2 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors"
    >
      {image && (
        <div className="aspect-video bg-gray-100 dark:bg-gray-800 w-full overflow-hidden">
          <img
            src={getMediaUrl(image)}
            alt=""
            onError={() => setImageFailed(true)}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      )}
      <div className="p-3" dir="auto">
        {decodedSiteName && (
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-0.5 truncate">
            {decodedSiteName}
          </p>
        )}
        <p className="font-medium text-gray-900 dark:text-gray-100 text-sm line-clamp-2">{decodedTitle}</p>
        {decodedDescription && (
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-0.5 line-clamp-2">{decodedDescription}</p>
        )}
        <p className="text-link text-xs mt-1.5 truncate" dir="ltr">{url}</p>
      </div>
    </a>
  );
};

export default LinkPreview;
